import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  LineController,
  BarController,
  DoughnutController,
  PieController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import StatCard from './dashboard/components/StatCard';
import FilterPanel from './dashboard/components/FilterPanel';
import {
  fetchHeaderStats,
  fetchProductStats,
  fetchCountryStats,
  fetchDashboardData,
  fetchFilteredDashboardData,
  fetchPlatformByDate,
  fetchProductByPlatform,
  fetchProductVariantStats,
  fetchCountryByDate,
  fetchCountryProductStats,
  fetchBenefice,
  fetchBeneficeByProduct,
  fetchChargesByTypeAndDate,
  fetchHeaderStatsByDate,
  fetchStuckOrders,
} from '../../services/dashboardService';
import { tunisianCities } from '../../utils/cities';
import { getAllPlatforms } from './platform/controller/platform-controller';
import {fetchAllProducts} from './controllers/products-controller';
import CustomDateInput from '../../components/CustomDateInput';

ChartJS.register(
  LineController,
  BarController,
  DoughnutController,
  PieController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard = () => {
  // State management
  const [headerStats, setHeaderStats] = useState(null);
  const [productStats, setProductStats] = useState(null);
  const [countryStats, setCountryStats] = useState(null);
  const [platformStats, setPlatformStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});

  // Individual section data
  const [filteredProductData, setFilteredProductData] = useState([]);
  const [filteredGeoData, setFilteredGeoData] = useState([]);
  const [filteredPlatformData, setFilteredPlatformData] = useState([]);
  const [productVariantData, setProductVariantData] = useState([]);
  const [allPlatforms, setAllPlatforms] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  // Loading states
  const [platformLoading, setPlatformLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [variantLoading, setVariantLoading] = useState(false);

  // Add new state for country product analysis
  const [countryProductData, setCountryProductData] = useState([]);
  const [countryProductLoading, setCountryProductLoading] = useState(false);
  const [hiddenCities, setHiddenCities] = useState(new Set()); // Track hidden cities

  // New states for default date filters
  const [platformFilter, setPlatformFilter] = useState({ startDate: '', endDate: '' });
  const [geoFilter, setGeoFilter] = useState({ startDate: '', endDate: '' });
  const [countryProductFilter, setCountryProductFilter] = useState({ startDate: '', endDate: '' });

  const [variantFilter, setVariantFilter] = useState({ startDate: '', endDate: '' });
  const [variantChartData, setVariantChartData] = useState(null);
  const [variantChartLoading, setVariantChartLoading] = useState(false);

  const [productByPlatformFilter, setProductByPlatformFilter] = useState({ startDate: '', endDate: '', platformId: '', country: '' });
  const [productByPlatformChartData, setProductByPlatformChartData] = useState(null);
  const [productByPlatformChartLoading, setProductByPlatformChartLoading] = useState(false);

  // Benefice chart state
  const [beneficeFilter, setBeneficeFilter] = useState({ startDate: '', endDate: '', productId: '', platformId: '' });
  const [beneficeGroupBy, setBeneficeGroupBy] = useState('day');
  const [beneficeData, setBeneficeData] = useState(null);
  const [beneficeByProductData, setBeneficeByProductData] = useState(null);
  const [beneficeLoading, setBeneficeLoading] = useState(false);

  // Benefice searchable dropdown state
  const [beneficePlatformSearch, setBeneficePlatformSearch] = useState('');
  const [beneficePlatformDropdownOpen, setBeneficePlatformDropdownOpen] = useState(false);
  const beneficePlatformDropdownRef = React.useRef(null);
  const [beneficeProductSearch, setBeneficeProductSearch] = useState('');
  const [beneficeProductDropdownOpen, setBeneficeProductDropdownOpen] = useState(false);
  const beneficeProductDropdownRef = React.useRef(null);

  // Charges chart state
  const [chargesFilter, setChargesFilter] = useState({ startDate: '', endDate: '' });
  const [chargesGroupBy, setChargesGroupBy] = useState('day');
  const [chargesTypeData, setChargesTypeData] = useState(null);
  const [chargesLoading, setChargesLoading] = useState(false);

  // Header date filter state
  const [headerFilter, setHeaderFilter] = useState({ startDate: '', endDate: '' });
  const [headerLoading, setHeaderLoading] = useState(false);

  // Stuck orders state
  const [stuckFilter, setStuckFilter] = useState({ productId: '', days: 7 });
  const [stuckData, setStuckData] = useState(null);
  const [stuckLoading, setStuckLoading] = useState(false);
  const [stuckOrdersExpanded, setStuckOrdersExpanded] = useState(false);
  const [stuckProductSearch, setStuckProductSearch] = useState('');
  const [stuckProductDropdownOpen, setStuckProductDropdownOpen] = useState(false);
  const stuckProductDropdownRef = useRef(null);

  // Close the stuck product dropdown when clicking outside
  useEffect(() => {
    if (!stuckProductDropdownOpen) return;
    const handler = (e) => {
      if (stuckProductDropdownRef.current && !stuckProductDropdownRef.current.contains(e.target)) {
        setStuckProductDropdownOpen(false);
        setStuckProductSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [stuckProductDropdownOpen]);

  // Close benefice platform dropdown on outside click
  useEffect(() => {
    if (!beneficePlatformDropdownOpen) return;
    const handler = (e) => {
      if (beneficePlatformDropdownRef.current && !beneficePlatformDropdownRef.current.contains(e.target)) {
        setBeneficePlatformDropdownOpen(false);
        setBeneficePlatformSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [beneficePlatformDropdownOpen]);

  // Close benefice product dropdown on outside click
  useEffect(() => {
    if (!beneficeProductDropdownOpen) return;
    const handler = (e) => {
      if (beneficeProductDropdownRef.current && !beneficeProductDropdownRef.current.contains(e.target)) {
        setBeneficeProductDropdownOpen(false);
        setBeneficeProductSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [beneficeProductDropdownOpen]);

  // Date formatting function for MM-DD-YYYY format
  const formatDateForAPI = (dateString) => {
    if (!dateString) return null;

    console.log('Input date string:', dateString);

    if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
      console.log('Already in MM-DD-YYYY format:', dateString);
      return dateString;
    }

    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const formatted = `${month}-${day}-${year}`;
      console.log('Converted YYYY-MM-DD to MM-DD-YYYY:', dateString, '→', formatted);
      return formatted;
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return null;
      }

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      const formatted = `${month}-${day}-${year}`;
      console.log('Parsed and formatted to MM-DD-YYYY:', dateString, '→', formatted);
      return formatted;
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return null;
    }
  };

  // Get city name from city code
  const getCityName = (cityCode) => {
    const city = tunisianCities.find(city => city.Code === cityCode);
    return city ? city.EnName : cityCode;
  };

  // Handle global filters change
  const handleFiltersChange = (newFilters) => {
    console.log('🔄 Global filters changed:', newFilters);
    setFilters(newFilters);
  };

  // Helper to get default dates (last 5 months)
  const getDefaultFilterDates = () => {
    const now = new Date();
    const endDate = now;
    const startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1); // 5 months ago, first day
    // Format as yyyy-mm-dd for input[type=date]
    const pad = n => String(n).padStart(2, '0');
    const format = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return {
      startDate: format(startDate),
      endDate: format(endDate),
    };
  };

  // Load initial data (current month)
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Load filtered data when global filters change
  useEffect(() => {
    if (filters.dateDebut && filters.dateFin) {
      loadFilteredData();
    }
  }, [filters]);

  // Fetch platforms on mount
  useEffect(() => {
    getAllPlatforms().then(setAllPlatforms).catch(() => setAllPlatforms([]));
  }, []);

  // Fetch all products for product select (use fetchAllProducts instead of fetchProducts)
  useEffect(() => {
    fetchAllProducts()
      .then(res => {
        if (Array.isArray(res)) {
          setAllProducts(res);
        } else {
          setAllProducts([]);
        }
      })
      .catch(() => setAllProducts([]));
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const dashboardData = await fetchDashboardData();

      setHeaderStats(dashboardData.headerStats);
      setProductStats(dashboardData.productStats);
      setCountryStats(dashboardData.countryStats);

      // Initialize filtered data with current month data - DON'T call API on initial load
      if (dashboardData.productStats) {
        setFilteredProductData(dashboardData.productStats.livred || []);
      }

      if (dashboardData.countryStats) {
        setFilteredGeoData(dashboardData.countryStats.livred || []);
      }

      // ✅ FIXED: Don't initialize platform data with API call on page load
      // Just set empty array until user applies filters
      setFilteredPlatformData([]);
      setPlatformStats([]);

    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFilteredData = async () => {
    try {
      if (filters.dateDebut && filters.dateFin) {
        console.log('🔄 Loading filtered data with filters:', filters);

        // Load platform data with date filters
        const platformData = await fetchPlatformByDate(filters.dateDebut, filters.dateFin);
        setPlatformStats(platformData);
        setFilteredPlatformData(platformData);

        // Load country data with date filters
        const countryData = await fetchCountryByDate(filters.dateDebut, filters.dateFin);
        setFilteredGeoData(countryData.livred || []);
      }
    } catch (err) {
      console.error('Error loading filtered data:', err);
    }
  };

  const handleVariantChartFilter = async (filterData) => {
    if (filterData.dateDebut && filterData.dateFin && filterData.productId) {
      setVariantChartLoading(true);
      try {
        const res = await fetchProductVariantStats(filterData.productId, filterData.dateDebut, filterData.dateFin);
        setVariantChartData(res);
      } catch {
        setVariantChartData(null);
      } finally {
        setVariantChartLoading(false);
      }
    } else {
      setVariantChartData(null);
    }
  };

  // Platform filter handler - FIXED to match Variant filter approach
  const handlePlatformFilter = async (filterData) => {
    if (filterData.dateDebut && filterData.dateFin) {
      try {
        setPlatformLoading(true);
        // Pass country and platformId to your API call
        const response = await fetchPlatformByDate(
          filterData.dateDebut,
          filterData.dateFin,
        );
        setFilteredPlatformData(response);
      } catch (error) {
        setFilteredPlatformData([]);
      } finally {
        setPlatformLoading(false);
      }
    } else {
      setFilteredPlatformData([]);
    }
  };

  // Product filter handler
  const handleProductFilter = async (filterData) => {
    if (filterData.dateDebut && filterData.dateFin) {
      try {
        setProductLoading(true);

        console.log('🎯 Product filter - receiving already formatted dates:', filterData);

        // ✅ FIXED: No more double formatting - dates are already in MM-DD-YYYY format
        // Use the current month product data as filtered data doesn't have date endpoints
        // In a real scenario, you'd have a product endpoint with date filters
        setFilteredProductData(productStats?.livred || []);
      } catch (error) {
        console.error('Error fetching product data:', error);
        setFilteredProductData(productStats?.livred || []);
      } finally {
        setProductLoading(false);
      }
    } else {
      setFilteredProductData(productStats?.livred || []);
    }
  };

  const handleProductByPlatformFilter = async (filterData) => {
  if (filterData.dateDebut && filterData.dateFin && filterData.platformId) {
    setProductByPlatformChartLoading(true);
    try {
      const res = await fetchProductByPlatform(
        filterData.platformId,
        filterData.country || '',
        filterData.dateDebut,
        filterData.dateFin
      );
      setProductByPlatformChartData(res);
    } catch {
      setProductByPlatformChartData(null);
    } finally {
      setProductByPlatformChartLoading(false);
    }
  } else {
    setProductByPlatformChartData(null);
  }
};

  // Benefice filter handler — platform required, product optional.
  // Uses Benefice/ByProduct when a product is selected, Benefice otherwise.
  // Grouping (day/week/month) is done on the frontend from Details[].
  const handleBeneficeFilter = async (filterData) => {
    const dateDebut = filterData?.dateDebut;
    const dateFin = filterData?.dateFin;
    const platformId = filterData?.platformId ?? beneficeFilter.platformId;
    const productId = filterData?.productId ?? beneficeFilter.productId;

    if (!dateDebut || !dateFin || !platformId) {
      setBeneficeData(null);
      setBeneficeByProductData(null);
      return;
    }

    setBeneficeLoading(true);
    try {
      if (productId) {
        const data = await fetchBeneficeByProduct(dateDebut, dateFin, platformId, productId);
        setBeneficeData(data);
      } else {
        const data = await fetchBenefice(dateDebut, dateFin, platformId);
        setBeneficeData(data);
      }
      setBeneficeByProductData(null);
    } catch {
      setBeneficeData(null);
      setBeneficeByProductData(null);
    } finally {
      setBeneficeLoading(false);
    }
  };

  // Charges filter handler
  const handleChargesFilter = async (filterData, groupBy) => {
    const gb = groupBy || chargesGroupBy;
    if (filterData.dateDebut && filterData.dateFin) {
      setChargesLoading(true);
      try {
        const data = await fetchChargesByTypeAndDate(filterData.dateDebut, filterData.dateFin, gb);
        setChargesTypeData(data);
      } catch {
        setChargesTypeData(null);
      } finally {
        setChargesLoading(false);
      }
    } else {
      setChargesTypeData(null);
    }
  };

  // Header stats filter handler — also refetches Product and Country top charts
  const handleHeaderFilter = async () => {
    if (!headerFilter.startDate || !headerFilter.endDate) return;
    setHeaderLoading(true);
    try {
      const fmt = (ds) => {
        if (!ds) return null;
        if (ds.match(/^\d{4}-\d{2}-\d{2}$/)) { const [y, m, d] = ds.split('-'); return `${m}-${d}-${y}`; }
        return ds;
      };
      const s = fmt(headerFilter.startDate);
      const e = fmt(headerFilter.endDate);
      const [header, product, country] = await Promise.all([
        fetchHeaderStatsByDate(s, e),
        fetchProductStats(s, e),
        fetchCountryStats(s, e),
      ]);
      setHeaderStats(header);
      setProductStats(product);
      setCountryStats(country);
      // Sync filtered side state so downstream chart helpers stay consistent
      setFilteredProductData(product?.livred || []);
      setFilteredGeoData(country || {});
    } catch (err) {
      console.error('Header filter error:', err);
    } finally {
      setHeaderLoading(false);
    }
  };

  const handleResetHeaderFilter = async () => {
    setHeaderFilter({ startDate: '', endDate: '' });
    setHeaderLoading(true);
    try {
      const [header, product, country] = await Promise.all([
        fetchHeaderStatsByDate(),
        fetchProductStats(),
        fetchCountryStats(),
      ]);
      setHeaderStats(header);
      setProductStats(product);
      setCountryStats(country);
      setFilteredProductData(product?.livred || []);
      setFilteredGeoData(country || {});
    } catch (err) {
      console.error('Reset header filter error:', err);
    } finally {
      setHeaderLoading(false);
    }
  };

  // Stuck orders filter handler
  const handleStuckFilter = async () => {
    if (!stuckFilter.productId) return;
    setStuckLoading(true);
    try {
      const data = await fetchStuckOrders(stuckFilter.productId, stuckFilter.days || 7);
      setStuckData(data);
      setStuckOrdersExpanded(false);
    } catch {
      setStuckData(null);
    } finally {
      setStuckLoading(false);
    }
  };

  const getEtatLabel = (etat) => {
    const map = { readyToShip: 'Prêt à expédier', shipped: 'Expédié', return: 'Retour' };
    return map[etat] || etat;
  };

  const getEtatColor = (etat) => {
    const map = { readyToShip: 'bg-blue-100 text-blue-800', shipped: 'bg-yellow-100 text-yellow-800', return: 'bg-red-100 text-red-800' };
    return map[etat] || 'bg-gray-100 text-gray-800';
  };

  const getProductMixedChartData = () => {
  if (!productStats?.livred) return null;
  // Get all unique product references from all arrays
  const allRefs = [
    ...new Set([
      ...productStats.livred.map(p => p.referenceProduct),
      ...(productStats.retours || []).map(p => p.referenceProduct),
      ...(productStats.tauxLivred || []).map(p => p.referenceProduct),
    ]),
  ];
  const livredMap = Object.fromEntries((productStats.livred || []).map(p => [p.referenceProduct, p.nombreVente]));
  const retoursMap = Object.fromEntries((productStats.retours || []).map(p => [p.referenceProduct, p.nombreVente]));
  const tauxMap = Object.fromEntries((productStats.tauxLivred || []).map(p => [p.referenceProduct, p.taux]));

  // Sort by livred (descending)
  const sortedRefs = [...allRefs].sort((a, b) => (livredMap[b] || 0) - (livredMap[a] || 0));

  return {
    labels: sortedRefs,
    datasets: [
      {
        type: 'bar',
        label: 'Livrées',
        data: sortedRefs.map(ref => livredMap[ref] || 0),
        backgroundColor: '#174540',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: 'Retours',
        data: sortedRefs.map(ref => retoursMap[ref] || 0),
        backgroundColor: '#ef4444',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Taux de Livraison (%)',
        data: sortedRefs.map(ref => tauxMap[ref] || 0),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#f59e0b',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        yAxisID: 'y1',
      },
    ],
  };
};

  const getProductByPlatformChartData = () => {
  if (!productByPlatformChartData) return null;
  const allRefs = [
    ...new Set([
      ...(productByPlatformChartData.livred || []).map(p => p.referenceProduct),
      ...(productByPlatformChartData.retours || []).map(p => p.referenceProduct),
      ...(productByPlatformChartData.tauxLivred || []).map(p => p.referenceProduct),
    ]),
  ];
  const livredMap = Object.fromEntries((productByPlatformChartData.livred || []).map(p => [p.referenceProduct, p.nombreVente]));
  const retoursMap = Object.fromEntries((productByPlatformChartData.retours || []).map(p => [p.referenceProduct, p.nombreVente]));
  const tauxMap = Object.fromEntries((productByPlatformChartData.tauxLivred || []).map(p => [p.referenceProduct, p.taux]));

  // Sort by livred (descending)
  const sortedRefs = [...allRefs].sort((a, b) => (livredMap[b] || 0) - (livredMap[a] || 0));

  return {
    labels: sortedRefs,
    datasets: [
      {
        type: 'bar',
        label: 'Livrées',
        data: sortedRefs.map(ref => livredMap[ref] || 0),
        backgroundColor: '#174540',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: 'Retours',
        data: sortedRefs.map(ref => retoursMap[ref] || 0),
        backgroundColor: '#ef4444',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Taux de Livraison (%)',
        data: sortedRefs.map(ref => tauxMap[ref] || 0),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#f59e0b',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        yAxisID: 'y1',
      },
    ],
  };
};

  const getVariantChartData = () => {
  if (!variantChartData) return null;
  const allRefs = [
    ...new Set([
      ...(variantChartData.livred || []).map(p => p.referenceProduct),
      ...(variantChartData.retours || []).map(p => p.referenceProduct),
      ...(variantChartData.tauxLivred || []).map(p => p.referenceProduct),
    ]),
  ];
  const livredMap = Object.fromEntries((variantChartData.livred || []).map(p => [p.referenceProduct, p.nombreVente]));
  const retoursMap = Object.fromEntries((variantChartData.retours || []).map(p => [p.referenceProduct, p.nombreVente]));
  const tauxMap = Object.fromEntries((variantChartData.tauxLivred || []).map(p => [p.referenceProduct, p.taux]));

  // Sort by livred (descending)
  const sortedRefs = [...allRefs].sort((a, b) => (livredMap[b] || 0) - (livredMap[a] || 0));

  return {
    labels: sortedRefs,
    datasets: [
      {
        type: 'bar',
        label: 'Livrées',
        data: sortedRefs.map(ref => livredMap[ref] || 0),
        backgroundColor: '#174540',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: 'Retours',
        data: sortedRefs.map(ref => retoursMap[ref] || 0),
        backgroundColor: '#ef4444',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Taux de Livraison (%)',
        data: sortedRefs.map(ref => tauxMap[ref] || 0),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#f59e0b',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        yAxisID: 'y1',
      },
    ],
  };
};

  // Group Details[] by day/week/month on the frontend
  const groupBeneficeDetails = (details, groupBy) => {
    if (!Array.isArray(details) || details.length === 0) return [];
    const keyFor = (dateStr) => {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      const pad = n => String(n).padStart(2, '0');
      if (groupBy === 'month') return `${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
      if (groupBy === 'week') {
        // ISO week number
        const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        const day = tmp.getUTCDay() || 7;
        tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
        const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
        const week = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
        return `S${week}-${tmp.getUTCFullYear()}`;
      }
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
    };
    const map = new Map();
    for (const row of details) {
      const k = keyFor(row.date);
      if (!map.has(k)) map.set(k, { period: k, beneficeProduit: 0, totalCharges: 0, nombreRetour: 0, coutRetour: 0, benefice: 0, sortKey: new Date(row.date).getTime() });
      const acc = map.get(k);
      acc.beneficeProduit += row.beneficeProduit || 0;
      acc.totalCharges += row.totalCharges || 0;
      acc.nombreRetour += row.nombreRetour || 0;
      acc.coutRetour += row.coutRetour || 0;
      acc.benefice += row.benefice || 0;
      if (new Date(row.date).getTime() < acc.sortKey) acc.sortKey = new Date(row.date).getTime();
    }
    return Array.from(map.values()).sort((a, b) => a.sortKey - b.sortKey);
  };

  // Benefice chart data — works with the new API shape { details, total... }
  const getBeneficeByProductChartData = () => {
    if (!beneficeData || !Array.isArray(beneficeData.details) || beneficeData.details.length === 0) return null;
    const grouped = groupBeneficeDetails(beneficeData.details, beneficeGroupBy);
    if (grouped.length === 0) return null;
    return {
      labels: grouped.map(g => g.period),
      datasets: [
        { type: 'bar', label: 'Bénéfice Produit', data: grouped.map(g => Number(g.beneficeProduit.toFixed(2))), backgroundColor: '#174540', borderRadius: 6, yAxisID: 'y' },
        { type: 'bar', label: 'Charges', data: grouped.map(g => Number(g.totalCharges.toFixed(2))), backgroundColor: '#f59e0b', borderRadius: 6, yAxisID: 'y' },
        { type: 'bar', label: 'Coût Retour', data: grouped.map(g => Number(g.coutRetour.toFixed(2))), backgroundColor: '#ef4444', borderRadius: 6, yAxisID: 'y' },
        { type: 'line', label: 'Bénéfice Net', data: grouped.map(g => Number(g.benefice.toFixed(2))), borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.1)', borderWidth: 3, fill: false, tension: 0.4, pointBackgroundColor: '#10b981', pointBorderColor: '#fff', pointBorderWidth: 2, pointRadius: 5, yAxisID: 'y' },
      ],
    };
  };

  // Charges by type chart data
  const getChargesTypeChartData = () => {
    // Backend returns { details, totalParType, total } — chart uses the details array
    const items = Array.isArray(chargesTypeData) ? chargesTypeData : chargesTypeData?.details;
    if (!items || items.length === 0) return null;
    const types = [...new Set(items.map(c => c.typeCharge))];
    const periods = [...new Set(items.map(c => c.period || c.date))].sort();
    const colors = ['#174540', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#10b981', '#6366f1'];
    return {
      labels: periods.map(p => {
        try { const d = new Date(p); return isNaN(d) ? p : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }); } catch { return p; }
      }),
      datasets: types.map((type, i) => ({
        label: type,
        data: periods.map(p => {
          const item = items.find(c => c.typeCharge === type && (c.period === p || c.date === p));
          return item ? item.total : 0;
        }),
        backgroundColor: colors[i % colors.length],
        borderRadius: 4,
      })),
    };
  };

  const getCountryMixedChartData = () => {
  if (!countryStats?.livred) return null;
  // Get all unique country codes from all arrays
  const allCountries = [
    ...new Set([
      ...countryStats.livred.map(c => c.country),
      ...(countryStats.retours || []).map(c => c.country),
      ...(countryStats.tauxLivred || []).map(c => c.referenceProduct),
    ]),
  ];
  const livredMap = Object.fromEntries((countryStats.livred || []).map(c => [c.country, c.nombreVente]));
  const retoursMap = Object.fromEntries((countryStats.retours || []).map(c => [c.country, c.nombreVente]));
  const tauxMap = Object.fromEntries((countryStats.tauxLivred || []).map(c => [c.referenceProduct, c.taux]));

  // Sort by livred (descending)
  const sortedCountries = [...allCountries].sort((a, b) => (livredMap[b] || 0) - (livredMap[a] || 0));

  return {
    labels: sortedCountries.map(getCityName),
    datasets: [
      {
        type: 'bar',
        label: 'Livrées',
        data: sortedCountries.map(code => livredMap[code] || 0),
        backgroundColor: '#2563eb',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: 'Retours',
        data: sortedCountries.map(code => retoursMap[code] || 0),
        backgroundColor: '#ef4444',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Taux de Livraison (%)',
        data: sortedCountries.map(code => tauxMap[code] || 0),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#f59e0b',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        yAxisID: 'y1',
      },
    ],
  };
};

  // --- Chart options for both ---
  const mixedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 13 },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255,255,255,0.95)',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function (context) {
            if (context.dataset.type === 'line') {
              return `${context.dataset.label}: ${context.parsed.y}%`;
            }
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6b7280', maxRotation: 45, minRotation: 0, font: { size: 12 } },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        grid: { color: 'rgba(156,163,175,0.1)' },
        ticks: { color: '#6b7280' },
        title: { display: true, text: 'Nombre', color: '#6b7280' },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        max: 100,
        grid: { drawOnChartArea: false },
        ticks: { color: '#f59e0b', callback: value => value + '%' },
        title: { display: true, text: 'Taux de livraison (%)', color: '#f59e0b' },
      },
    },
    interaction: { mode: 'index', intersect: false },
  };

  const getFilteredGeoMixedChartData = () => {
  // filteredGeoData is the full API response (object with livred, retours, tauxLivred arrays)
  if (!filteredGeoData?.livred) return null;
  const allCountries = [
    ...new Set([
      ...(filteredGeoData.livred || []).map(c => c.country),
      ...(filteredGeoData.retours || []).map(c => c.country),
      ...(filteredGeoData.tauxLivred || []).map(c => c.referenceProduct),
    ]),
  ];
  const livredMap = Object.fromEntries((filteredGeoData.livred || []).map(c => [c.country, c.nombreVente]));
  const retoursMap = Object.fromEntries((filteredGeoData.retours || []).map(c => [c.country, c.nombreVente]));
  const tauxMap = Object.fromEntries((filteredGeoData.tauxLivred || []).map(c => [c.referenceProduct, c.taux]));

  // Sort by livred (descending)
  const sortedCountries = [...allCountries].sort((a, b) => (livredMap[b] || 0) - (livredMap[a] || 0));

  return {
    labels: sortedCountries.map(getCityName),
    datasets: [
      {
        type: 'bar',
        label: 'Livrées',
        data: sortedCountries.map(code => livredMap[code] || 0),
        backgroundColor: '#2563eb',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: 'Retours',
        data: sortedCountries.map(code => retoursMap[code] || 0),
        backgroundColor: '#ef4444',
        borderRadius: 6,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Taux de Livraison (%)',
        data: sortedCountries.map(code => tauxMap[code] || 0),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#f59e0b',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        yAxisID: 'y1',
      },
    ],
  };
};

  // Geographic filter handler - FIXED to match Variant filter approach  
  const handleGeoFilter = async (filterData) => {
    if (filterData.dateDebut && filterData.dateFin) {
      try {
        setGeoLoading(true);

        console.log('🎯 Geo filter - receiving already formatted dates:', filterData);

        // ✅ FIXED: No need to format again - MiniFilter already sends MM-DD-YYYY format
        // Just pass the dates directly as received from MiniFilter
        const response = await fetchCountryByDate(filterData.dateDebut, filterData.dateFin);
        setFilteredGeoData(response);
      } catch (error) {
        console.error('Error fetching geo data:', error);
        setFilteredGeoData(countryStats?.livred || []);
      } finally {
        setGeoLoading(false);
      }
    } else {
      setFilteredGeoData(countryStats?.livred || []);
    }
  };

  // Product variant filter handler
  const handleVariantFilter = async (filterData) => {
    if (filterData.dateDebut && filterData.dateFin && filterData.productId) {
      try {
        setVariantLoading(true);

        console.log('🎯 Variant filter - receiving already formatted dates:', filterData);

        const response = await fetchProductVariantStats(filterData.productId, filterData.dateDebut, filterData.dateFin);
        setProductVariantData(response.livred || []);
      } catch (error) {
        console.error('Error fetching variant data:', error);
        setProductVariantData([]);
      } finally {
        setVariantLoading(false);
      }
    } else {
      setProductVariantData([]);
    }
  };

  // Country Product filter handler
  const handleCountryProductFilter = async (filterData) => {
    if (filterData.dateDebut && filterData.dateFin && filterData.productId) {
      try {
        setCountryProductLoading(true);

        console.log('🎯 Country Product filter - receiving already formatted dates:', filterData);

        const response = await fetchCountryProductStats(filterData.productId, filterData.dateDebut, filterData.dateFin);
        setCountryProductData(response);
        setHiddenCities(new Set()); // Reset hidden cities when new data loads
      } catch (error) {
        console.error('Error fetching country product data:', error);
        setCountryProductData({});
      } finally {
        setCountryProductLoading(false);
      }
    } else {
      setCountryProductData({});
      setHiddenCities(new Set());
    }
  };

  // Toggle city visibility
  const toggleCityVisibility = (cityCode) => {
    const newHiddenCities = new Set(hiddenCities);
    if (newHiddenCities.has(cityCode)) {
      newHiddenCities.delete(cityCode);
    } else {
      newHiddenCities.add(cityCode);
    }
    setHiddenCities(newHiddenCities);
  };

  // Get country product chart data with mixed chart (bars + line)
  const getCountryProductChartData = () => {
    if (!countryProductData || countryProductData.length === 0) return null;

    // Process the data to group by country AND variant
    const countryVariantGroups = {};

    // Process livred data - group by country AND variant
    countryProductData.livred?.forEach(item => {
      const key = `${item.country}-${item.variantName}`;
      if (!countryVariantGroups[key]) {
        countryVariantGroups[key] = {
          country: item.country,
          variantName: item.variantName,
          livred: 0,
          retours: 0,
          tauxLivred: 0
        };
      }
      countryVariantGroups[key].livred += item.nombreVente || 0;
    });

    // Process retours data - group by country AND variant
    countryProductData.retours?.forEach(item => {
      const key = `${item.country}-${item.variantName}`;
      if (!countryVariantGroups[key]) {
        countryVariantGroups[key] = {
          country: item.country,
          variantName: item.variantName,
          livred: 0,
          retours: 0,
          tauxLivred: 0
        };
      }
      countryVariantGroups[key].retours += item.nombreVente || 0;
    });

    // Calculate delivery rate per country-variant combination
    const tauxByCountryVariant = {};
    countryProductData.tauxLivred?.forEach(item => {
      const key = `${item.country}-${item.referenceVariant}`;
      if (!tauxByCountryVariant[key]) {
        tauxByCountryVariant[key] = [];
      }
      tauxByCountryVariant[key].push(item.taux || 0);
    });

    Object.keys(tauxByCountryVariant).forEach(key => {
      if (countryVariantGroups[key]) {
        const rates = tauxByCountryVariant[key];
        countryVariantGroups[key].tauxLivred = rates.reduce((a, b) => a + b, 0) / rates.length;
      }
    });

    // Filter out hidden cities and convert to array
    const visibleData = Object.values(countryVariantGroups)
      .filter(item => !hiddenCities.has(item.country))
      .sort((a, b) => (b.livred + b.retours) - (a.livred + a.retours));

    if (visibleData.length === 0) return null;

    // Create labels showing both city and variant
    const labels = visibleData.map(item => {
      const cityName = getCityName(item.country);
      const variantName = item.variantName || 'N/A';
      return `${cityName} - ${variantName}`;
    });

    return {
      labels: labels,
      datasets: [
        {
          type: 'bar',
          label: 'Livrées',
          data: visibleData.map(item => item.livred),
          backgroundColor: '#174540',
          borderColor: '#174540',
          borderWidth: 1,
          yAxisID: 'y',
          borderRadius: 4,
        },
        {
          type: 'bar',
          label: 'Retours',
          data: visibleData.map(item => item.retours),
          backgroundColor: '#ef4444',
          borderColor: '#ef4444',
          borderWidth: 1,
          yAxisID: 'y',
          borderRadius: 4,
        },
        {
          type: 'line',
          label: 'Taux de Livraison (%)',
          data: visibleData.map(item => parseFloat(item.tauxLivred.toFixed(1))),
          borderColor: '#f59e0b',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#f59e0b',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 6,
          yAxisID: 'y1',
        },
      ],
    };
  };

  // Chart data generators for NON-FILTERED charts (original data)
  const getProductsChartData = () => {
    if (!productStats?.livred || productStats.livred.length === 0) return null;

    const topProducts = productStats.livred.slice(0, 8);
    const colors = [
      '#174540', '#1e5651', '#256862', '#2c7a73',
      '#338c84', '#3a9e95', '#41b0a6', '#48c2b7'
    ];

    return {
      labels: topProducts.map(p => p.referenceProduct),
      datasets: [
        {
          label: 'Ventes Livrées',
          data: topProducts.map(p => p.nombreVente),
          backgroundColor: colors,
          borderWidth: 0,
          borderRadius: 8,
        },
      ],
    };
  };

  const getCountriesChartData = () => {
    if (!countryStats?.livred || countryStats.livred.length === 0) return null;

    const topCountries = countryStats.livred.slice(0, 8);
    const colors = [
      '#174540', '#1e5651', '#256862', '#2c7a73',
      '#338c84', '#3a9e95', '#41b0a6', '#48c2b7'
    ];

    return {
      labels: topCountries.map(c => getCityName(c.country)),
      datasets: [
        {
          data: topCountries.map(c => c.nombreVente),
          backgroundColor: colors,
          borderWidth: 2,
          borderColor: '#ffffff',
          hoverOffset: 8,
        },
      ],
    };
  };
const getFilteredPlatformChartData = () => {
  if (!filteredPlatformData || filteredPlatformData.length === 0) return null;

  // Sort platforms by chiffre d'affaires (descending)
  const sortedData = [...filteredPlatformData].sort((a, b) => (b.chiffreDaffaire || 0) - (a.chiffreDaffaire || 0));

  const colors = [
    '#174540', '#1e5651', '#256862', '#2c7a73',
    '#338c84', '#3a9e95', '#41b0a6', '#48c2b7'
  ];

  return {
    labels: sortedData.map(p => p.plateformName),
    datasets: [
      {
        label: "Chiffre d'affaires (EUR)",
        data: sortedData.map(p => p.chiffreDaffaire),
        backgroundColor: colors.slice(0, sortedData.length),
        borderWidth: 0,
        borderRadius: 8,
      },
    ],
  };
};

  const getFilteredGeoChartData = () => {
    if (!filteredGeoData || filteredGeoData.length === 0) return null;

    const topCountries = filteredGeoData.slice(0, 8);
    const colors = [
      '#174540', '#1e5651', '#256862', '#2c7a73',
      '#338c84', '#3a9e95', '#41b0a6', '#48c2b7'
    ];

    return {
      labels: topCountries.map(c => getCityName(c.country)),
      datasets: [
        {
          data: topCountries.map(c => c.nombreVente),
          backgroundColor: colors,
          borderWidth: 2,
          borderColor: '#ffffff',
          hoverOffset: 8,
        },
      ],
    };
  };

  const getDeliveryRateChartData = () => {
    if (!productStats?.tauxLivred || productStats.tauxLivred.length === 0) return null;

    const topProducts = productStats.tauxLivred.slice(0, 8);

    return {
      labels: topProducts.map(p => p.referenceProduct),
      datasets: [
        {
          label: 'Taux de Livraison (%)',
          data: topProducts.map(p => p.taux),
          backgroundColor: topProducts.map(p => {
            if (p.taux >= 90) return '#10b981';
            if (p.taux >= 80) return '#f59e0b';
            return '#ef4444';
          }),
          borderWidth: 0,
          borderRadius: 8,
        },
      ],
    };
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
        },
        ticks: {
          color: '#6b7280',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6b7280',
          maxRotation: 45,
        },
      },
    },
  };

  // Mini Filter Component - FIXED DATE FORMATTING
  const MiniFilter = ({
    onFilterChange,
    sectionName,
    isLoading = false,
    showProductSelect = false,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    children, // <-- add this
  }) => {
    const [selectedProduct, setSelectedProduct] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
    const [dateError, setDateError] = useState('');
    const productDropdownRef = React.useRef(null);

    // Close dropdown on outside click
    React.useEffect(() => {
      const handleClickOutside = (e) => {
        if (productDropdownRef.current && !productDropdownRef.current.contains(e.target)) {
          setIsProductDropdownOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredProducts = allProducts.filter(p =>
      !productSearch || p.reference?.toLowerCase().includes(productSearch.toLowerCase()) || p.name?.toLowerCase().includes(productSearch.toLowerCase())
    );

    const selectedProductObj = allProducts.find(p => String(p.id) === String(selectedProduct));

    const validateDates = (start, end) => {
      if (!start || !end) return '';

      const startDate = new Date(start);
      const endDate = new Date(end);

      if (startDate > endDate) {
        return 'La date de début doit être antérieure à la date de fin';
      }

      return '';
    };

    const formatDateForAPI = (dateString) => {
      if (!dateString) return null;

      // If it's already in MM-DD-YYYY format, return as is
      if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
        return dateString;
      }

      // If it's in YYYY-MM-DD format (from HTML date input), convert it
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        const formatted = `${month}-${day}-${year}`;
        return formatted;
      }

      // Try parsing as Date object and format
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return null;
        }

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        const formatted = `${month}-${day}-${year}`;
        return formatted;
      } catch {
        return null;
      }
    };

    const handleApply = () => {
      const validation = validateDates(startDate, endDate);
      if (validation) {
        setDateError(validation);
        return;
      }

      setDateError('');
      const filterData = {
        dateDebut: formatDateForAPI(startDate),
        dateFin: formatDateForAPI(endDate),
      };
      if (showProductSelect && selectedProduct) {
        filterData.productId = selectedProduct;
      }
      onFilterChange(filterData);
    };

    const handleClear = () => {
      setStartDate('');
      setEndDate('');
      setSelectedProduct('');
      setProductSearch('');
      setDateError('');
      onFilterChange({});
    };

    return (
      <div className="mb-4 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700 flex items-center">
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
            </svg>
            Filtrer {sectionName}
          </h4>
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#174540] border-t-transparent"></div>
          )}
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date début</label>
              <CustomDateInput
                value={startDate}
                onChange={v => setStartDate(v)}
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date fin</label>
              <CustomDateInput
                value={endDate}
                onChange={v => setEndDate(v)}
                disabled={isLoading}
                min={startDate || undefined}
              />
            </div>
          </div>
          {showProductSelect && (
            <div ref={productDropdownRef} className="relative">
              <label className="block text-xs font-medium text-gray-600 mb-1">Produit *</label>
              <div
                onClick={() => !isLoading && setIsProductDropdownOpen(!isProductDropdownOpen)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white cursor-pointer flex items-center justify-between"
              >
                <span className={selectedProductObj ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedProductObj ? `${selectedProductObj.reference} - ${selectedProductObj.name}` : 'Sélectionner un produit'}
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isProductDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {isProductDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Rechercher un produit..."
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-[#174540] focus:border-[#174540]"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-44">
                    <div
                      onClick={() => { setSelectedProduct(''); setIsProductDropdownOpen(false); setProductSearch(''); }}
                      className="px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 cursor-pointer"
                    >
                      Aucun produit
                    </div>
                    {filteredProducts.map(product => (
                      <div
                        key={product.id}
                        onClick={() => { setSelectedProduct(String(product.id)); setIsProductDropdownOpen(false); setProductSearch(''); }}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-[#174540]/10 ${String(product.id) === String(selectedProduct) ? 'bg-[#174540]/10 font-medium text-[#174540]' : 'text-gray-700'}`}
                      >
                        {product.reference} - {product.name}
                      </div>
                    ))}
                    {filteredProducts.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-400 text-center">Aucun résultat</div>
                    )}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">* Veuillez sélectionner un produit pour voir les variantes</p>
            </div>
          )}
          {children} {/* <-- ADD THIS LINE to render dropdowns passed as children */}
        </div>

        {dateError && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
            <p className="text-xs text-red-600">{dateError}</p>
          </div>
        )}

        <div className="flex items-center justify-end space-x-2 mt-3">
          <button
            onClick={handleClear}
            disabled={isLoading}
            className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Effacer
          </button>
          <button
            onClick={handleApply}
            disabled={isLoading || !startDate || !endDate || dateError}
            className="px-3 py-1.5 bg-[#174540] text-white text-xs rounded-md hover:bg-[#0d2926] transition-colors disabled:opacity-50 flex items-center space-x-1"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                <span>Chargement...</span>
              </>
            ) : (
              <span>Appliquer</span>
            )}
          </button>
        </div>
      </div>
    );
  };

  const MiniFilterWithPlatform = ({
  onFilterChange,
  sectionName,
  isLoading = false,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  platformId,
  setPlatformId,
  allPlatforms,
  country,           // <-- add this
  setCountry         // <-- add this
}) => {
  const [platSearch, setPlatSearch] = useState('');
  const [platDropdownOpen, setPlatDropdownOpen] = useState(false);
  const platDropdownRef = React.useRef(null);
  const [dateError, setDateError] = useState('');

  React.useEffect(() => {
    if (!platDropdownOpen) return;
    const handler = (e) => {
      if (platDropdownRef.current && !platDropdownRef.current.contains(e.target)) {
        setPlatDropdownOpen(false);
        setPlatSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [platDropdownOpen]);

  const validateDates = (start, end) => {
    if (!start || !end) return '';
    const startD = new Date(start);
    const endD = new Date(end);
    if (startD > endD) return 'La date de début doit être antérieure à la date de fin';
    return '';
  };

  const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) return dateString;
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${month}-${day}-${year}`;
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return null;
    }
  };

  const handleApply = () => {
    const validation = validateDates(startDate, endDate);
    if (validation) {
      setDateError(validation);
      return;
    }
    setDateError('');
    onFilterChange({
      dateDebut: formatDateForAPI(startDate),
      dateFin: formatDateForAPI(endDate),
      platformId,
      country,
    });
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setPlatformId('');
    setDateError('');
    setCountry && setCountry('');
    onFilterChange({});
  };

  return (
    <div className="mb-4 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700 flex items-center">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
          </svg>
          Filtrer {sectionName}
        </h4>
        {isLoading && (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#174540] border-t-transparent"></div>
        )}
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date début</label>
            <CustomDateInput
              value={startDate}
              onChange={v => setStartDate(v)}
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date fin</label>
            <CustomDateInput
              value={endDate}
              onChange={v => setEndDate(v)}
              disabled={isLoading}
              min={startDate || undefined}
            />
          </div>
        </div>
        <div ref={platDropdownRef} className="relative">
          <label className="block text-xs font-medium text-gray-600 mb-1">Plateforme *</label>
          <div
            onClick={() => !isLoading && setPlatDropdownOpen(!platDropdownOpen)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white cursor-pointer flex items-center justify-between"
          >
            <span className={platformId ? 'text-gray-900' : 'text-gray-400'}>
              {(() => { const p = allPlatforms.find(p => String(p.id) === String(platformId)); return p ? `${p.name}${p.typePlateform ? ` (${p.typePlateform})` : ''}` : 'Sélectionner une plateforme'; })()}
            </span>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${platDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {platDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
              <div className="p-2 border-b">
                <input
                  type="text"
                  value={platSearch}
                  onChange={(e) => setPlatSearch(e.target.value)}
                  placeholder="Rechercher une plateforme..."
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-[#174540] focus:border-[#174540]"
                  autoFocus
                />
              </div>
              <div className="overflow-y-auto max-h-44">
                <div
                  onClick={() => { setPlatformId(''); setPlatDropdownOpen(false); setPlatSearch(''); }}
                  className="px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 cursor-pointer"
                >
                  Aucune plateforme
                </div>
                {allPlatforms.filter(p => !platSearch || p.name?.toLowerCase().includes(platSearch.toLowerCase()) || p.typePlateform?.toLowerCase().includes(platSearch.toLowerCase())).map(platform => (
                  <div
                    key={platform.id}
                    onClick={() => { setPlatformId(String(platform.id)); setPlatDropdownOpen(false); setPlatSearch(''); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-[#174540]/10 ${String(platform.id) === String(platformId) ? 'bg-[#174540]/10 font-medium text-[#174540]' : 'text-gray-700'}`}
                  >
                    {platform.name} {platform.typePlateform && <span className="text-xs text-gray-400 ml-1">({platform.typePlateform})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">* Veuillez sélectionner une plateforme</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Gouvernorat</label>
          <select
            value={country}
            onChange={e => setCountry(e.target.value)}
            disabled={isLoading}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
          >
            <option value="">Tous</option>
            {tunisianCities.map(city => (
              <option key={city.Code} value={city.Code}>
                {city.EnName}
              </option>
            ))}
          </select>
        </div>
      </div>
      {dateError && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
          <p className="text-xs text-red-600">{dateError}</p>
        </div>
      )}
      <div className="flex items-center justify-end space-x-2 mt-3">
        <button
          onClick={handleClear}
          disabled={isLoading}
          className="px-3 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Effacer
        </button>
        <button
          onClick={handleApply}
          disabled={isLoading || !startDate || !endDate || !platformId || dateError}
          className="px-3 py-1.5 bg-[#174540] text-white text-xs rounded-md hover:bg-[#0d2926] transition-colors disabled:opacity-50 flex items-center space-x-1"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
              <span>Chargement...</span>
            </>
          ) : (
            <span>Appliquer</span>
          )}
        </button>
      </div>
    </div>
  );
};

  // City Toggle Component
  const CityTogglePanel = () => {
    if (!countryProductData || !countryProductData.livred || countryProductData.livred.length === 0) return null;

    // Get unique countries from the data
    const uniqueCountries = [...new Set([
      ...(countryProductData.livred?.map(item => item.country) || []),
      ...(countryProductData.retours?.map(item => item.country) || [])
    ])];

    return (
      <div className="mb-4 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="text-sm font-medium text-blue-800 mb-3 flex items-center">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Visibilité des Régions ({uniqueCountries.length} régions)
        </h4>
        <div className="flex flex-wrap gap-2">
          {uniqueCountries.map(country => {
            const isHidden = hiddenCities.has(country);
            const cityName = getCityName(country);

            // Calculate total sales and unique variants for this country
            const totalSales = (countryProductData.livred?.filter(item => item.country === country).reduce((sum, item) => sum + (item.nombreVente || 0), 0) || 0);
            const uniqueVariants = [...new Set(countryProductData.livred?.filter(item => item.country === country).map(item => item.variantName) || [])];

            return (
              <button
                key={country}
                onClick={() => toggleCityVisibility(country)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-all ${isHidden
                    ? 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'
                    : 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200'
                  }`}
                title={`${cityName}: ${uniqueVariants.length} variante(s) - ${uniqueVariants.join(', ')}`}
              >
                <span className="flex items-center space-x-1">
                  <span>{isHidden ? '👁️‍🗨️' : '👁️'}</span>
                  <span className="font-medium">{cityName}</span>
                  <span className="text-xs opacity-75">
                    ({totalSales} | {uniqueVariants.length}v)
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-blue-600">
            💡 Cliquez sur une région pour la masquer/afficher. Nombre = ventes | v = variantes
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setHiddenCities(new Set())}
              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            >
              Tout afficher
            </button>
            <button
              onClick={() => setHiddenCities(new Set(uniqueCountries))}
              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              Tout masquer
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-3 border-[#174540] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-xl p-8 shadow-lg border max-w-md w-full">
          <div className="text-red-500 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Erreur</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadDashboardData}
            className="px-6 py-2 bg-[#174540] text-white rounded-lg hover:bg-[#0d2926] transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 sm:px-6 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">Tableau de Bord</h1>
              <p className="text-sm sm:text-base lg:text-lg text-gray-600">Aperçu complet de vos performances commerciales</p>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="px-3 sm:px-4 py-1 sm:py-2 bg-green-100 text-green-800 rounded-full text-xs sm:text-sm font-medium shadow-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>En direct</span>
                </div>
              </div>
              <button
                onClick={loadDashboardData}
                className="p-2 sm:p-3 bg-white rounded-xl shadow-sm border hover:bg-gray-50 transition-all hover:shadow-md"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Header Stats */}
        {/* Date Filter for Header Stats */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date début</label>
              <input
                type="date"
                value={headerFilter.startDate}
                onChange={e => setHeaderFilter(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#174540] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date fin</label>
              <input
                type="date"
                value={headerFilter.endDate}
                onChange={e => setHeaderFilter(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#174540] focus:border-transparent"
              />
            </div>
            <button
              onClick={handleHeaderFilter}
              disabled={headerLoading || !headerFilter.startDate || !headerFilter.endDate}
              className="px-4 py-2 text-sm bg-[#174540] text-white rounded-lg hover:bg-[#1e5c55] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {headerLoading ? 'Chargement...' : 'Appliquer'}
            </button>
            {(headerFilter.startDate || headerFilter.endDate) && (
              <button
                onClick={handleResetHeaderFilter}
                disabled={headerLoading}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Réinitialiser
              </button>
            )}
            {(!headerFilter.startDate && !headerFilter.endDate) && (
              <span className="text-xs text-gray-400 italic">Mois en cours par défaut</span>
            )}
          </div>
        </div>

        {headerStats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
            <StatCard
              title="Chiffre d'Affaires"
              value={headerStats.chiffreAffaire}
              subtitle="TND"
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              change={`Aujourd'hui : ${Number(headerStats.chiffreAffaireToday || 0).toLocaleString()} TND`}
              changeType="increase"
              color="emerald"
            />

            <StatCard
              title="Total Clients"
              value={headerStats.totalClient}
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
              change="Actifs"
              changeType="increase"
              color="blue"
            />

            <StatCard
              title="Commandes Livrées"
              value={headerStats.totalOrderLivred}
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              }
              change="Succès"
              changeType="increase"
              color="purple"
            />

            <StatCard
              title="Retours"
              value={headerStats.totalOrderRetour}
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                </svg>
              }
              change="À traiter"
              changeType="decrease"
              color="amber"
            />
          </div>
        )}

        {/* Charts Layout */}
        <div className="space-y-6 sm:space-y-8">

          {/* NON-FILTERED CHARTS - TOP SECTION */}
          <div>

            {/* Top Products & Geographic Distribution (NO FILTERS) */}
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
              {/* Mixed Product Chart (ALL DATA) */}
              <div>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8 h-[500px] flex flex-col">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Ventes & Retours par Produit</h3>
                    <p className="text-sm text-gray-500">Livrées, retours et taux de livraison (tous produits)</p>
                  </div>
                  <div className="flex-1 min-h-0">
                    {getProductMixedChartData() ? (
                      <Bar data={getProductMixedChartData()} options={mixedChartOptions} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">Aucune donnée</div>
                    )}
                  </div>
                </div>
              </div>
              {/* Mixed Country Chart (ALL DATA) */}
              <div>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8 h-[500px] flex flex-col">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Ventes & Retours par Région</h3>
                    <p className="text-sm text-gray-500">Livrées, retours et taux de livraison (toutes régions)</p>
                  </div>
                  <div className="flex-1 min-h-0">
                    {getCountryMixedChartData() ? (
                      <Bar data={getCountryMixedChartData()} options={mixedChartOptions} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">Aucune donnée</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stuck Orders Section - after Product & Region charts */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6">
            <div className="mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Commandes Bloquées par Produit</h3>
              <p className="text-sm text-gray-500">Commandes en readyToShip / shipped / return depuis plus de N jours</p>
            </div>

            {/* Filter Row */}
            <div className="flex flex-wrap items-end gap-3 mb-6">
              <div className="flex-1 min-w-[200px]" ref={stuckProductDropdownRef}>
                <label className="block text-xs font-medium text-gray-600 mb-1">Produit *</label>
                {/* Custom searchable dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setStuckProductDropdownOpen(prev => !prev)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:border-[#174540] focus:outline-none focus:ring-2 focus:ring-[#174540] focus:border-transparent text-left"
                  >
                    <span className={`truncate ${stuckFilter.productId ? 'text-gray-900' : 'text-gray-400'}`}>
                      {stuckFilter.productId
                        ? (() => { const p = allProducts.find(x => String(x.id) === String(stuckFilter.productId)); return p ? `${p.reference} - ${p.name}` : 'Sélectionner un produit'; })()
                        : 'Sélectionner un produit'}
                    </span>
                    <svg className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ml-2 ${stuckProductDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {stuckProductDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-[100] bg-white border border-gray-200 rounded-xl shadow-2xl">
                      {/* Search input */}
                      <div className="p-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <input
                            autoFocus
                            type="text"
                            placeholder="Rechercher un produit..."
                            value={stuckProductSearch}
                            onChange={e => setStuckProductSearch(e.target.value)}
                            className="flex-1 bg-transparent text-sm outline-none text-gray-900 placeholder-gray-400"
                          />
                          {stuckProductSearch && (
                            <button type="button" onClick={() => setStuckProductSearch('')} className="text-gray-400 hover:text-gray-600">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Options list */}
                      <ul className="max-h-60 overflow-y-auto py-1">
                        {allProducts
                          .filter(p => {
                            const q = stuckProductSearch.toLowerCase();
                            return !q || p.reference?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q);
                          })
                          .map(p => (
                            <li key={p.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  setStuckFilter(prev => ({ ...prev, productId: String(p.id) }));
                                  setStuckProductDropdownOpen(false);
                                  setStuckProductSearch('');
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-[#174540] hover:text-white transition-colors ${String(stuckFilter.productId) === String(p.id) ? 'bg-[#e8f4f2] text-[#174540] font-medium' : 'text-gray-800'}`}
                              >
                                <span className="font-medium">{p.reference}</span>
                                {p.name && <span className="text-xs ml-1 opacity-75">— {p.name}</span>}
                              </button>
                            </li>
                          ))}
                        {allProducts.filter(p => {
                          const q = stuckProductSearch.toLowerCase();
                          return !q || p.reference?.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q);
                        }).length === 0 && (
                          <li className="px-4 py-3 text-sm text-gray-400 text-center">Aucun produit trouvé</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bloqué depuis (jours)</label>
                <input
                  type="number"
                  min={1}
                  value={stuckFilter.days}
                  onChange={e => setStuckFilter(prev => ({ ...prev, days: parseInt(e.target.value) || 7 }))}
                  className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#174540] focus:border-transparent"
                />
              </div>
              <button
                onClick={handleStuckFilter}
                disabled={stuckLoading || !stuckFilter.productId}
                className="px-4 py-2 text-sm bg-[#174540] text-white rounded-lg hover:bg-[#1e5c55] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {stuckLoading ? 'Chargement...' : 'Rechercher'}
              </button>
            </div>

            {/* Results */}
            {stuckData && (
              <div>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Total pièces bloquées</p>
                    <p className="text-2xl font-bold text-gray-900">{stuckData.totalPieces}</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                    <p className="text-xs text-blue-600 mb-1">Prêt à expédier</p>
                    <p className="text-2xl font-bold text-blue-900">{stuckData.readyToShipPieces}</p>
                  </div>
                  <div className="bg-yellow-50 rounded-xl p-3 text-center border border-yellow-100">
                    <p className="text-xs text-yellow-600 mb-1">Expédié</p>
                    <p className="text-2xl font-bold text-yellow-900">{stuckData.shippedPieces}</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
                    <p className="text-xs text-red-600 mb-1">Retour</p>
                    <p className="text-2xl font-bold text-red-900">{stuckData.retourPieces}</p>
                  </div>
                </div>

                {/* Variants Table */}
                {stuckData.variants && stuckData.variants.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Détail par variante</h4>
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Variante</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-blue-600 uppercase tracking-wider">Prêt expédier</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-yellow-600 uppercase tracking-wider">Expédié</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-red-600 uppercase tracking-wider">Retour</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Total</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {stuckData.variants.map((v, i) => (
                            <tr key={i} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 font-medium text-gray-900">{v.variantName || '—'}</td>
                              <td className="px-4 py-3 text-center text-blue-700 font-semibold">{v.readyToShipPieces}</td>
                              <td className="px-4 py-3 text-center text-yellow-700 font-semibold">{v.shippedPieces}</td>
                              <td className="px-4 py-3 text-center text-red-700 font-semibold">{v.retourPieces}</td>
                              <td className="px-4 py-3 text-center font-bold text-gray-900">{v.totalPieces}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Orders Table (collapsible) */}
                {stuckData.orders && stuckData.orders.length > 0 && (
                  <div>
                    <button
                      onClick={() => setStuckOrdersExpanded(prev => !prev)}
                      className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3 hover:text-[#174540] transition-colors"
                    >
                      <svg className={`w-4 h-4 transition-transform ${stuckOrdersExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Détail des commandes ({stuckData.orders.length})
                    </button>
                    {stuckOrdersExpanded && (
                      <div className="overflow-x-auto rounded-xl border border-gray-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Référence</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">État</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Variante</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Qté</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Jours bloqué</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {stuckData.orders.map((o, i) => (
                              <tr key={i} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 font-mono text-gray-900 font-medium">{o.reference}</td>
                                <td className="px-4 py-3 text-gray-600">{new Date(o.date).toLocaleDateString('fr-FR')}</td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getEtatColor(o.etat)}`}>
                                    {getEtatLabel(o.etat)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-gray-700">{o.variantName || '—'}</td>
                                <td className="px-4 py-3 text-center font-semibold text-gray-900">{o.quantite}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-bold ${o.daysStuck >= 14 ? 'bg-red-100 text-red-800' : o.daysStuck >= 7 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                    {o.daysStuck}j
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {stuckData.totalPieces === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="font-medium">Aucune commande bloquée pour ce produit</p>
                    <p className="text-sm mt-1">Depuis plus de {stuckFilter.days} jours</p>
                  </div>
                )}
              </div>
            )}

            {!stuckData && !stuckLoading && (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">Sélectionnez un produit et cliquez sur "Rechercher" pour voir les commandes bloquées</p>
              </div>
            )}
          </div>

          {/* FILTERED CHARTS SECTION */}
          <div>

            {/* Filtered Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
              {/* Platform Performance - WITH FILTER */}
              <div>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8 h-[550px] sm:h-[600px] lg:h-[650px] flex flex-col">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Performance des Plateformes</h3>
                    <p className="text-sm text-gray-500">Chiffre d'affaires par plateforme</p>
                  </div>

                  <MiniFilter
                    onFilterChange={() => {
                      handlePlatformFilter({
                        dateDebut: formatDateForAPI(platformFilter.startDate),
                        dateFin: formatDateForAPI(platformFilter.endDate),
                      });
                    }}
                    sectionName="les plateformes"
                    isLoading={platformLoading}
                    startDate={platformFilter.startDate}
                    endDate={platformFilter.endDate}
                    setStartDate={date => setPlatformFilter(prev => ({ ...prev, startDate: date }))}
                    setEndDate={date => setPlatformFilter(prev => ({ ...prev, endDate: date }))}
                  />
                  <div className="flex-1 min-h-0">
                    {getFilteredPlatformChartData() ? (
                      <Bar
                        data={getFilteredPlatformChartData()}
                        options={chartOptions}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">Aucune donnée</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Geographic - WITH FILTER */}
              <div>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8 h-[550px] sm:h-[600px] lg:h-[650px] flex flex-col">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Répartition Géographique Filtrée</h3>
                    <p className="text-sm text-gray-500">Ventes par gouvernorat (avec filtres)</p>
                  </div>

                  <MiniFilter
                    onFilterChange={filterData => {
                      setGeoFilter(prev => ({ ...prev, ...filterData }));
                      handleGeoFilter(filterData);
                    }}
                    sectionName="par région"
                    isLoading={geoLoading}
                    startDate={geoFilter.startDate}
                    endDate={geoFilter.endDate}
                    setStartDate={date => setGeoFilter(prev => ({ ...prev, startDate: date }))}
                    setEndDate={date => setGeoFilter(prev => ({ ...prev, endDate: date }))}
                  />

                  <div className="flex-1 min-h-0">
                    {getFilteredGeoMixedChartData() ? (
                      <Bar data={getFilteredGeoMixedChartData()} options={mixedChartOptions} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">Aucune donnée</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Product Variants by Product & Products by Platform - SIDE BY SIDE */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-8">
              {/* Product Variants by Product */}
              <div>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8 h-[650px] flex flex-col">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Ventes & Retours par Variante de Produit</h3>
                    <p className="text-sm text-gray-500">Livrées, retours et taux de livraison par variante</p>
                  </div>
                  <MiniFilter
                    onFilterChange={filterData => {
                      setVariantFilter(prev => ({ ...prev, ...filterData }));
                      handleVariantChartFilter(filterData);
                    }}
                    sectionName="par produit"
                    isLoading={variantChartLoading}
                    showProductSelect={true}
                    startDate={variantFilter.startDate}
                    endDate={variantFilter.endDate}
                    setStartDate={date => setVariantFilter(prev => ({ ...prev, startDate: date }))}
                    setEndDate={date => setVariantFilter(prev => ({ ...prev, endDate: date }))}
                  />
                  <div className="flex-1 min-h-0">
                    {getVariantChartData() ? (
                      <Bar data={getVariantChartData()} options={mixedChartOptions} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">Aucune donnée</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Products by Platform */}
              <div>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8 h-[650px] flex flex-col">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Ventes & Retours par Plateforme</h3>
                    <p className="text-sm text-gray-500">Livrées, retours et taux de livraison par plateforme</p>
                  </div>
                  <MiniFilterWithPlatform
                      onFilterChange={filterData => {
                        setProductByPlatformFilter(prev => ({ ...prev, ...filterData }));
                        handleProductByPlatformFilter(filterData);
                      }}
                      sectionName="par plateforme"
                      isLoading={productByPlatformChartLoading}
                      startDate={productByPlatformFilter.startDate}
                      endDate={productByPlatformFilter.endDate}
                      setStartDate={date => setProductByPlatformFilter(prev => ({ ...prev, startDate: date }))}
                      setEndDate={date => setProductByPlatformFilter(prev => ({ ...prev, endDate: date }))}
                      platformId={productByPlatformFilter.platformId}
                      setPlatformId={platformId => setProductByPlatformFilter(prev => ({ ...prev, platformId }))}
                      allPlatforms={allPlatforms}
                      country={productByPlatformFilter.country}
                      setCountry={country => setProductByPlatformFilter(prev => ({ ...prev, country }))}
                    >
                      {/* Platform dropdown */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Plateforme *</label>
                        <select
                          value={productByPlatformFilter.platformId}
                          onChange={e => setProductByPlatformFilter(prev => ({ ...prev, platformId: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                        >
                          <option value="">Sélectionner une plateforme</option>
                          {allPlatforms.map(platform => (
                            <option key={platform.id} value={platform.id}>
                              {platform.name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">* Veuillez sélectionner une plateforme</p>
                      </div>
                      {/* Country dropdown */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Gouvernorat</label>
                        <select
                          value={productByPlatformFilter.country}
                          onChange={e => setProductByPlatformFilter(prev => ({ ...prev, country: e.target.value }))}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                        >
                          <option value="">Tous</option>
                          {tunisianCities.map(city => (
                            <option key={city.Code} value={city.Code}>
                              {city.EnName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </MiniFilterWithPlatform>
                  <div className="flex-1 min-h-0">
                    {getProductByPlatformChartData() ? (
                      <Bar data={getProductByPlatformChartData()} options={mixedChartOptions} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">Aucune donnée</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Product Variants by Country Chart - REMOVE HORIZONTAL SCROLL */}
            <div className="grid grid-cols-1 mt-6 sm:mt-8">
              <div>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8 h-[850px] sm:h-[900px] lg:h-[950px] flex flex-col">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Analyse Produit par Région</h3>
                    <p className="text-sm text-gray-500">Livrées, retours et taux de livraison par gouvernorat</p>
                  </div>

                  <MiniFilter
                    onFilterChange={filterData => {
                      setCountryProductFilter(prev => ({ ...prev, ...filterData }));
                      handleCountryProductFilter(filterData);
                    }}
                    sectionName="l'analyse produit"
                    isLoading={variantLoading}
                    showProductSelect={true}
                    startDate={countryProductFilter.startDate}
                    endDate={countryProductFilter.endDate}
                    setStartDate={date => setCountryProductFilter(prev => ({ ...prev, startDate: date }))}
                    setEndDate={date => setCountryProductFilter(prev => ({ ...prev, endDate: date }))}
                  />

                  {/* City Toggle Panel */}
                  <CityTogglePanel />

                  {/* ✅ FIXED SIZE CHART CONTAINER (NO horizontal scroll) */}
                  <div className="flex-1 min-h-0">
                    {getCountryProductChartData() ? (
                      <Bar
                        data={getCountryProductChartData()}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top',
                              labels: {
                                usePointStyle: true,
                                padding: 20,
                                font: {
                                  size: 12,
                                },
                              },
                            },
                            tooltip: {
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              titleColor: '#1f2937',
                              bodyColor: '#1f2937',
                              borderColor: '#e5e7eb',
                              borderWidth: 1,
                              cornerRadius: 8,
                              padding: 12,
                              callbacks: {
                                title: function (context) {
                                  return context[0].label;
                                },
                                label: function (context) {
                                  const datasetLabel = context.dataset.label;
                                  const value = context.parsed.y;

                                  if (datasetLabel === 'Taux de Livraison (%)') {
                                    return `${datasetLabel}: ${value}%`;
                                  }

                                  return `${datasetLabel}: ${value}`;
                                }
                              }
                            },
                          },
                          scales: {
                            x: {
                              grid: {
                                display: false,
                              },
                              ticks: {
                                color: '#6b7280',
                                maxRotation: 45,
                                minRotation: 0,
                                font: {
                                  size: 11,
                                },
                              },
                            },
                            y: {
                              type: 'linear',
                              display: true,
                              position: 'left',
                              beginAtZero: true,
                              grid: {
                                color: 'rgba(156, 163, 175, 0.1)',
                              },
                              ticks: {
                                color: '#6b7280',
                              },
                              title: {
                                display: true,
                                text: 'Nombre de commandes',
                                color: '#6b7280',
                              },
                            },
                            y1: {
                              type: 'linear',
                              display: true,
                              position: 'right',
                              beginAtZero: true,
                              max: 100,
                              grid: {
                                drawOnChartArea: false,
                              },
                              ticks: {
                                color: '#f59e0b',
                                callback: function (value) {
                                  return value + '%';
                                },
                              },
                              title: {
                                display: true,
                                text: 'Taux de livraison (%)',
                                color: '#f59e0b',
                              },
                            },
                          },
                          interaction: {
                            mode: 'index',
                            intersect: false
                          },
                        }}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">Aucune donnée disponible</h4>
                          <p className="text-sm text-gray-500 mb-4">
                            Veuillez sélectionner une période et un produit pour voir l'analyse par région
                          </p>
                          <div className="text-xs text-gray-400">
                            <p>• Sélectionnez une date de début et de fin</p>
                            <p>• Choisissez un produit spécifique</p>
                            <p>• Cliquez sur "Appliquer" pour charger les données</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* BENEFICE & CHARGES CHARTS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-6 sm:mt-8">
              {/* Benefice Chart */}
              <div>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8 h-[750px] flex flex-col">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Bénéfice par Produit</h3>
                    <p className="text-sm text-gray-500">Revenu, coût, charges et bénéfice net</p>
                  </div>
                  <MiniFilter
                    onFilterChange={filterData => {
                      setBeneficeFilter(prev => ({ ...prev, ...filterData }));
                      handleBeneficeFilter({ ...filterData, platformId: beneficeFilter.platformId, productId: beneficeFilter.productId });
                    }}
                    sectionName="le bénéfice"
                    isLoading={beneficeLoading}
                    startDate={beneficeFilter.startDate}
                    endDate={beneficeFilter.endDate}
                    setStartDate={date => setBeneficeFilter(prev => ({ ...prev, startDate: date }))}
                    setEndDate={date => setBeneficeFilter(prev => ({ ...prev, endDate: date }))}
                  >
                    {/* Group by toggle (frontend-only grouping) */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Grouper par</label>
                      <div className="flex space-x-1">
                        {[{ value: 'day', label: 'Jour' }, { value: 'week', label: 'Semaine' }, { value: 'month', label: 'Mois' }].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setBeneficeGroupBy(opt.value)}
                            className={`flex-1 px-2 py-1.5 text-xs rounded-md transition-colors ${beneficeGroupBy === opt.value ? 'bg-[#174540] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Platform filter (REQUIRED) */}
                    <div ref={beneficePlatformDropdownRef} className="relative">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Plateforme <span className="text-red-500">*</span>
                      </label>
                      <div
                        onClick={() => !beneficeLoading && setBeneficePlatformDropdownOpen(!beneficePlatformDropdownOpen)}
                        className={`w-full px-2 py-1.5 text-sm border rounded-md bg-white cursor-pointer flex items-center justify-between ${!beneficeFilter.platformId ? 'border-red-300' : 'border-gray-300'}`}
                      >
                        <span className={beneficeFilter.platformId ? 'text-gray-900' : 'text-gray-400'}>
                          {(() => { const p = allPlatforms.find(p => String(p.id) === String(beneficeFilter.platformId)); return p ? `${p.name}${p.typePlateform ? ` (${p.typePlateform})` : ''}` : 'Sélectionner une plateforme'; })()}
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${beneficePlatformDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {beneficePlatformDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              value={beneficePlatformSearch}
                              onChange={(e) => setBeneficePlatformSearch(e.target.value)}
                              placeholder="Rechercher une plateforme..."
                              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-[#174540] focus:border-[#174540]"
                              autoFocus
                            />
                          </div>
                          <div className="overflow-y-auto max-h-44">
                            <div
                              onClick={() => {
                                setBeneficeFilter(prev => ({ ...prev, platformId: '' }));
                                setBeneficePlatformDropdownOpen(false);
                                setBeneficePlatformSearch('');
                                setBeneficeData(null);
                                setBeneficeByProductData(null);
                              }}
                              className="px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 cursor-pointer"
                            >
                              Aucune plateforme
                            </div>
                            {allPlatforms.filter(p => !beneficePlatformSearch || p.name?.toLowerCase().includes(beneficePlatformSearch.toLowerCase()) || p.typePlateform?.toLowerCase().includes(beneficePlatformSearch.toLowerCase())).map(p => (
                              <div
                                key={p.id}
                                onClick={() => {
                                  const newPlatformId = String(p.id);
                                  setBeneficeFilter(prev => ({ ...prev, platformId: newPlatformId }));
                                  setBeneficePlatformDropdownOpen(false);
                                  setBeneficePlatformSearch('');
                                  if (beneficeFilter.startDate && beneficeFilter.endDate && newPlatformId) {
                                    const fmtDate = (ds) => {
                                      if (!ds) return null;
                                      if (ds.match(/^\d{4}-\d{2}-\d{2}$/)) { const [y, m, d] = ds.split('-'); return `${m}-${d}-${y}`; }
                                      return ds;
                                    };
                                    handleBeneficeFilter({
                                      dateDebut: fmtDate(beneficeFilter.startDate),
                                      dateFin: fmtDate(beneficeFilter.endDate),
                                      platformId: newPlatformId,
                                      productId: beneficeFilter.productId,
                                    });
                                  }
                                }}
                                className={`px-3 py-2 text-sm cursor-pointer hover:bg-[#174540]/10 ${String(p.id) === String(beneficeFilter.platformId) ? 'bg-[#174540]/10 font-medium text-[#174540]' : 'text-gray-700'}`}
                              >
                                {p.name} {p.typePlateform && <span className="text-xs text-gray-400 ml-1">({p.typePlateform})</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Product filter (optional) */}
                    <div ref={beneficeProductDropdownRef} className="relative">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Filtrer par produit (optionnel)</label>
                      <div
                        onClick={() => !(beneficeLoading || !beneficeFilter.platformId) && setBeneficeProductDropdownOpen(!beneficeProductDropdownOpen)}
                        className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white cursor-pointer flex items-center justify-between ${(beneficeLoading || !beneficeFilter.platformId) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      >
                        <span className={beneficeFilter.productId ? 'text-gray-900' : 'text-gray-400'}>
                          {(() => { const p = allProducts.find(p => String(p.id) === String(beneficeFilter.productId)); return p ? `${p.reference} - ${p.name}` : 'Tous les produits'; })()}
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${beneficeProductDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {beneficeProductDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              value={beneficeProductSearch}
                              onChange={(e) => setBeneficeProductSearch(e.target.value)}
                              placeholder="Rechercher un produit..."
                              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-[#174540] focus:border-[#174540]"
                              autoFocus
                            />
                          </div>
                          <div className="overflow-y-auto max-h-44">
                            <div
                              onClick={() => {
                                const newProductId = '';
                                setBeneficeFilter(prev => ({ ...prev, productId: newProductId }));
                                setBeneficeProductDropdownOpen(false);
                                setBeneficeProductSearch('');
                                if (beneficeFilter.startDate && beneficeFilter.endDate && beneficeFilter.platformId) {
                                  const fmtDate = (ds) => {
                                    if (!ds) return null;
                                    if (ds.match(/^\d{4}-\d{2}-\d{2}$/)) { const [y, m, d] = ds.split('-'); return `${m}-${d}-${y}`; }
                                    return ds;
                                  };
                                  handleBeneficeFilter({
                                    dateDebut: fmtDate(beneficeFilter.startDate),
                                    dateFin: fmtDate(beneficeFilter.endDate),
                                    platformId: beneficeFilter.platformId,
                                    productId: '',
                                  });
                                }
                              }}
                              className="px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 cursor-pointer"
                            >
                              Tous les produits
                            </div>
                            {allProducts.filter(p => !beneficeProductSearch || p.reference?.toLowerCase().includes(beneficeProductSearch.toLowerCase()) || p.name?.toLowerCase().includes(beneficeProductSearch.toLowerCase())).map(p => (
                              <div
                                key={p.id}
                                onClick={() => {
                                  const newProductId = String(p.id);
                                  setBeneficeFilter(prev => ({ ...prev, productId: newProductId }));
                                  setBeneficeProductDropdownOpen(false);
                                  setBeneficeProductSearch('');
                                  if (beneficeFilter.startDate && beneficeFilter.endDate && beneficeFilter.platformId) {
                                    const fmtDate = (ds) => {
                                      if (!ds) return null;
                                      if (ds.match(/^\d{4}-\d{2}-\d{2}$/)) { const [y, m, d] = ds.split('-'); return `${m}-${d}-${y}`; }
                                      return ds;
                                    };
                                    handleBeneficeFilter({
                                      dateDebut: fmtDate(beneficeFilter.startDate),
                                      dateFin: fmtDate(beneficeFilter.endDate),
                                      platformId: beneficeFilter.platformId,
                                      productId: newProductId,
                                    });
                                  }
                                }}
                                className={`px-3 py-2 text-sm cursor-pointer hover:bg-[#174540]/10 ${String(p.id) === String(beneficeFilter.productId) ? 'bg-[#174540]/10 font-medium text-[#174540]' : 'text-gray-700'}`}
                              >
                                {p.reference} - {p.name}
                              </div>
                            ))}
                            {allProducts.filter(p => !beneficeProductSearch || p.reference?.toLowerCase().includes(beneficeProductSearch.toLowerCase()) || p.name?.toLowerCase().includes(beneficeProductSearch.toLowerCase())).length === 0 && (
                              <div className="px-3 py-2 text-sm text-gray-400 text-center">Aucun résultat</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </MiniFilter>
                  {/* Summary cards */}
                  {beneficeData && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                      <div className="bg-green-50 rounded-lg p-2 text-center">
                        <p className="text-xs text-green-600">Bénéfice Produit</p>
                        <p className="text-sm font-bold text-green-900">{Number(beneficeData.totalBeneficeProduit || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-2 text-center">
                        <p className="text-xs text-amber-600">Charges</p>
                        <p className="text-sm font-bold text-amber-900">{Number(beneficeData.totalCharges || 0).toFixed(2)}</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-2 text-center">
                        <p className="text-xs text-red-600">Retours ({beneficeData.totalNombreRetour || 0})</p>
                        <p className="text-sm font-bold text-red-900">{Number(beneficeData.totalMontantRetour || 0).toFixed(2)}</p>
                      </div>
                      <div className={`rounded-lg p-2 text-center ${(beneficeData.totalBenefice || 0) >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                        <p className={`text-xs ${(beneficeData.totalBenefice || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Bénéfice Net</p>
                        <p className={`text-sm font-bold ${(beneficeData.totalBenefice || 0) >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>{Number(beneficeData.totalBenefice || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex-1 min-h-0">
                    {getBeneficeByProductChartData() ? (
                      <Bar data={getBeneficeByProductChartData()} options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: true, position: 'top', labels: { usePointStyle: true, padding: 15, font: { size: 12 } } },
                          tooltip: { backgroundColor: 'rgba(255,255,255,0.95)', titleColor: '#1f2937', bodyColor: '#1f2937', borderColor: '#e5e7eb', borderWidth: 1, cornerRadius: 8, padding: 12 },
                        },
                        scales: {
                          x: { grid: { display: false }, ticks: { color: '#6b7280', maxRotation: 45, font: { size: 11 } } },
                          y: { beginAtZero: true, grid: { color: 'rgba(156,163,175,0.1)' }, ticks: { color: '#6b7280' }, title: { display: true, text: 'Montant (TND)', color: '#6b7280' } },
                        },
                        interaction: { mode: 'index', intersect: false },
                      }} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400 text-center px-4">
                        {!beneficeFilter.platformId
                          ? 'Veuillez sélectionner une plateforme (obligatoire) et une période'
                          : 'Sélectionnez une période pour voir le bénéfice'}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Charges by Type Chart */}
              <div>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 lg:p-8 h-[750px] flex flex-col">
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Charges par Type</h3>
                    <p className="text-sm text-gray-500">Répartition des charges par type et période</p>
                  </div>
                  <MiniFilter
                    onFilterChange={filterData => {
                      setChargesFilter(prev => ({ ...prev, ...filterData }));
                      handleChargesFilter(filterData, chargesGroupBy);
                    }}
                    sectionName="les charges"
                    isLoading={chargesLoading}
                    startDate={chargesFilter.startDate}
                    endDate={chargesFilter.endDate}
                    setStartDate={date => setChargesFilter(prev => ({ ...prev, startDate: date }))}
                    setEndDate={date => setChargesFilter(prev => ({ ...prev, endDate: date }))}
                  >
                    {/* Group by toggle */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Grouper par</label>
                      <div className="flex space-x-1">
                        {[{ value: 'day', label: 'Jour' }, { value: 'week', label: 'Semaine' }, { value: 'month', label: 'Mois' }].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setChargesGroupBy(opt.value);
                              if (chargesFilter.startDate && chargesFilter.endDate) {
                                const formatDateForAPI = (ds) => {
                                  if (!ds) return null;
                                  if (ds.match(/^\d{2}-\d{2}-\d{4}$/)) return ds;
                                  if (ds.match(/^\d{4}-\d{2}-\d{2}$/)) { const [y, m, d] = ds.split('-'); return `${m}-${d}-${y}`; }
                                  return ds;
                                };
                                handleChargesFilter({ dateDebut: formatDateForAPI(chargesFilter.startDate), dateFin: formatDateForAPI(chargesFilter.endDate) }, opt.value);
                              }
                            }}
                            className={`flex-1 px-2 py-1.5 text-xs rounded-md transition-colors ${chargesGroupBy === opt.value ? 'bg-[#174540] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </MiniFilter>
                  <div className="flex-1 min-h-0">
                    {getChargesTypeChartData() ? (
                      <Bar data={getChargesTypeChartData()} options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: true, position: 'top', labels: { usePointStyle: true, padding: 15, font: { size: 12 } } },
                          tooltip: { backgroundColor: 'rgba(255,255,255,0.95)', titleColor: '#1f2937', bodyColor: '#1f2937', borderColor: '#e5e7eb', borderWidth: 1, cornerRadius: 8, padding: 12 },
                        },
                        scales: {
                          x: { stacked: true, grid: { display: false }, ticks: { color: '#6b7280', maxRotation: 45, font: { size: 11 } } },
                          y: { stacked: true, beginAtZero: true, grid: { color: 'rgba(156,163,175,0.1)' }, ticks: { color: '#6b7280' }, title: { display: true, text: 'Montant (TND)', color: '#6b7280' } },
                        },
                        interaction: { mode: 'index', intersect: false },
                      }} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">Sélectionnez une période pour voir les charges</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
